<template>
  <div
    class="thumbnails"
    @mousedown="() => setThumbnailsFocus(true)"
    v-contextmenu="contextmenusThumbnails"
  >
    <PanelMenu class="add-slide" :model="items" v-model:expandedKeys="expandedKeys" />

    <Dialog v-model:visible="slideLibraryVisible" @update:visible="(val) => { if (!val) slideLibraryChooser = false; }" modal :header="slideLibraryTitle" :style="{ width: '50rem' }">
      <FileManager @fileChosen="fileChosen" :override-active-folder="overrideActiveFolder"/>
    </Dialog>

    <Draggable
      class="thumbnail-list"
      :modelValue="slides"
      :animation="300"
      :scroll="true"
      :scrollSensitivity="50"
      :setData="null"
      @end="handleDragEnd"
      itemKey="id"
    >
      <template #item="{ element, index }">
        <div
          class="thumbnail-item"
          :class="{
            'active': slideIndex === index,
            'selected': selectedSlidesIndex.includes(index),
          }"
          @mousedown="$event => handleClickSlideThumbnail($event, index)"
          v-contextmenu="contextmenusThumbnailItem"
        >
          <div class="label" :class="{ 'offset-left': index >= 99 }">{{ fillDigit(index + 1, 2) }}</div>
          <div class="thumbnail">
            <ThumbnailSlide :slide="element" :size="120" :visible="index < slidesLoadLimit" />
            <div style="font-size: 8px; width: 100%; text-align: center; background-color: #EFEFEF">{{ element.name }}</div>
          </div>


        </div>
      </template>
    </Draggable>

    <div class="page-number">Slide {{slideIndex + 1}} / {{slides.length}}</div>

    <Dialog v-model:visible="showEditSlideName" modal header="Edit Slide Name" :style="{ width: '50rem' }">
      Edit Name
      <InputText v-model="slideName" />
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import {computed, defineEmits, nextTick, ref} from 'vue'
import {storeToRefs} from 'pinia'
import {useKeyboardStore, useMainStore, useSlidesStore, useAuthStore} from '@/store'
import {fillDigit} from '@/utils/common'
import {ContextmenuItem} from '@/components/Contextmenu/types'
import useSlideHandler from '@/hooks/useSlideHandler'
import useScreening from '@/hooks/useScreening'
import useLoadSlides from '@/hooks/useLoadSlides'

import Draggable from 'vuedraggable'
import ThumbnailSlide from '@/views/components/ThumbnailSlide/index.vue'
import FileManager from "@/views/components/element/FileManagerElement/index.vue";
import useAddSlidesOrElements from "@/hooks/useAddSlidesOrElements";
import {useMicroservice} from "@/hooks";
import SlideBackgroundChooser from "@/views/Editor/Canvas/SlideBackgroundChooser.vue";
import {handleKeyStrokes} from "@/utils/keyStrokesInput";

const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const keyboardStore = useKeyboardStore()
const authStore = useAuthStore()
const { selectedSlidesIndex: _selectedSlidesIndex, thumbnailsFocus, slideLibraryChooser, slideLibraryActive, mergeExistingSlide } = storeToRefs(mainStore)
const { slides, slideIndex, currentSlide } = storeToRefs(slidesStore)

const { ctrlKeyState, shiftKeyState } = storeToRefs(keyboardStore)
const slideLibraryVisible = ref()
const { slidesLoadLimit } = useLoadSlides()
const selectedSlidesIndex = computed(() => [..._selectedSlidesIndex.value, slideIndex.value])
const {savePresentation, librarySlides} = useMicroservice()
const {lpiUser} = storeToRefs(authStore)

const emit = defineEmits(['panel-close'])
const expandedKeys = ref({})
const slideLibraryTitle = ref('')
const overrideActiveFolder = ref(false)

const showEditSlideName = ref(false)
const items = ref([{
  label: 'Insert New Slide',
  icon: 'pi pi-plus',
  items: [
    {
      label: 'Blank slide',
      icon: 'pi pi-file',
      command: () => {
        createSlide()
        savePresentation()
        nextTick(()=> expandedKeys.value = {})
      }
    },
    ...(lpiUser.value ? [{
      label: 'Library Slide',
      icon: 'pi pi-cloud-download',
      command: () => {
        overrideActiveFolder.value = false
        slideLibraryVisible.value = true
        slideLibraryChooser.value = true
        slideLibraryActive.value = true
        slideLibraryTitle.value = 'Insert Library Slide'
        nextTick(()=> expandedKeys.value = {})
      }
    }]: []),
  ]
}])

const slideName = computed({
  get: () => {
      return slides.value[slideIndex.value].name
  },
  set: val => {
    slides.value[slideIndex.value].name = val
  }
})


//const slideName = computed(() => { return "test" })
const {addSlidesFromData } = useAddSlidesOrElements()

const fileChosen = async (file) => {
  const url = file.url

  let slides;

  slides = await librarySlides(url) ?? file.slides
  const masterSlideId = url.includes("Master Slides") ? file.id : null
  overrideActiveFolder.value ? mergeExistingSlide.value = true : mergeExistingSlide.value = false
  const newSlides = addSlidesFromData(slides, masterSlideId)

  if (!mergeExistingSlide.value) {
    slidesStore.addSlide(newSlides)
  } else {
    const _newSlides = JSON.parse(JSON.stringify(newSlides))
    const slideLayoutElement = _newSlides[0].elements.shift()
    const layoutRows = slideLayoutElement.layout.children

    const headerRow = layoutRows[0];
    const headerRowId = headerRow.children[0].id
    const headerDragDrop = newSlides[0].elements.find(item => item.id === headerRowId)
    const footerRow = layoutRows.length > 2 ? layoutRows[layoutRows.length - 1] : null;
    const footerRowId = footerRow.children[0].id
    const footerDragDrop = newSlides[0].elements.find(item => item.id === footerRowId)

    const currentSlideLayout = currentSlide.value.elements.find(item => item.type === 'slidelayout')
    currentSlideLayout.layout.children = addFirstAndLastLayout(headerRow, footerRow, currentSlideLayout.layout.children)
    if (headerDragDrop) currentSlide.value.elements.push(headerDragDrop)
    if (footerDragDrop) currentSlide.value.elements.push(footerDragDrop)

    currentSlide.value.masterSlideId = masterSlideId
  }

  slideLibraryChooser.value = false
  slideLibraryVisible.value = false

  await savePresentation()
}

const addFirstAndLastLayout = (firstObject, lastObject, targetArr) => {
  return [firstObject, ...targetArr, lastObject];
}

const applyLayout = () => {
  overrideActiveFolder.value = true
  slideLibraryVisible.value = true
  slideLibraryChooser.value = true
  slideLibraryActive.value = true
  slideLibraryTitle.value = 'Apply Layout to Slide'
}

const {
  copySlide,
  pasteSlide,
  createSlide,
  editSlideName,
  createSlideByTemplate,
  copyAndPasteSlide,
  deleteSlide,
  cutSlide,
  selectAllSlide,
  sortSlides,
} = useSlideHandler()

const showEditSlideNameDialog = () => {
  showEditSlideName.value = true
}

const saveSlideName = () => {
  editSlideName(slideName.value)
}

// switch page
const changeSlideIndex = (index: number) => {
  mainStore.setActiveElementIdList([])

  if (slideIndex.value === index) return
  slidesStore.updateSlideIndex(index)
}

// click on the thumbnail
const handleClickSlideThumbnail = (e: MouseEvent, index: number) => {
  const isMultiSelected = selectedSlidesIndex.value.length > 1

  if (isMultiSelected && selectedSlidesIndex.value.includes(index) && e.button !== 0) return

  // Hold down the Ctrl key, click the slide, and click the selected page again to cancel the selection
  if (ctrlKeyState.value) {
    if (slideIndex.value === index) {
      if (!isMultiSelected) return

      const newSelectedSlidesIndex = selectedSlidesIndex.value.filter(item => item !== index)
      mainStore.updateSelectedSlidesIndex(newSelectedSlidesIndex)
      changeSlideIndex(selectedSlidesIndex.value[0])
    }
    else {
      if (selectedSlidesIndex.value.includes(index)) {
        const newSelectedSlidesIndex = selectedSlidesIndex.value.filter(item => item !== index)
        mainStore.updateSelectedSlidesIndex(newSelectedSlidesIndex)
      }
      else {
        const newSelectedSlidesIndex = [...selectedSlidesIndex.value, index]
        mainStore.updateSelectedSlidesIndex(newSelectedSlidesIndex)
        changeSlideIndex(index)
      }
    }
  }
  // Hold down the Shift key to select all slides in the range
  else if (shiftKeyState.value) {
    if (slideIndex.value === index && !isMultiSelected) return

    let minIndex = Math.min(...selectedSlidesIndex.value)
    let maxIndex = index

    if (index < minIndex) {
      maxIndex = Math.max(...selectedSlidesIndex.value)
      minIndex = index
    }

    const newSelectedSlidesIndex = []
    for (let i = minIndex; i <= maxIndex; i++) newSelectedSlidesIndex.push(i)
    mainStore.updateSelectedSlidesIndex(newSelectedSlidesIndex)
    changeSlideIndex(index)
  }
  // Switch pages normally
  else {
    mainStore.updateSelectedSlidesIndex([])
    changeSlideIndex(index)
  }
}

// Set the focus state of the thumbnail toolbar (only in the focus state, the shortcut keys of this part will take effect)
const setThumbnailsFocus = (focus: boolean) => {
  if (thumbnailsFocus.value === focus) return
  mainStore.setThumbnailsFocus(focus)

  if (!focus) mainStore.updateSelectedSlidesIndex([])
}

// Data synchronization after dragging to adjust the order
const handleDragEnd = (eventData: { newIndex: number; oldIndex: number }) => {
  const { newIndex, oldIndex } = eventData
  sortSlides(newIndex, oldIndex)
}

const { enterScreening, enterScreeningFromStart } = useScreening()

const handleOutsideClick = (event: Event) => {

  // Replace 'your-specific-element-selector' with the actual selector of the element you want to exclude
  const excludedElement = document.querySelector('.nav-btn')

  if (excludedElement && excludedElement.contains(event.target as Element)) {
    return
  }

  setThumbnailsFocus(false)
}

/* Right click on Thumbnail menu */
const contextmenusThumbnails = (): ContextmenuItem[] => {
  return [
    {
      text: 'Paste',
      subText: 'Ctrl + V',
      handler: pasteSlide,
    },
    {
      text: 'Select All',
      subText: 'Ctrl + A',
      handler: selectAllSlide,
    },
    {
      text: 'Add Blank Slide',
      subText: 'Ctrl + Enter',
      handler: createSlide,
    },
    {
      text: 'Slideshow',
      subText: 'F5',
      handler: enterScreeningFromStart,
    },
  ]
}

/* Right click on thumbnail slide */
const contextmenusThumbnailItem = (): ContextmenuItem[] => {
  return [
    {
      text: 'Cut',
      subText: 'Ctrl + X',
      handler: cutSlide,
    },
    {
      text: 'Copy',
      subText: 'Ctrl + C',
      handler: copySlide,
    },
    {
      text: 'Paste',
      subText: 'Ctrl + V',
      handler: pasteSlide,
    },
    {
      text: 'Select All',
      subText: 'Ctrl + A',
      handler: selectAllSlide,
    },
    { divider: true },
    {
      text: 'Add Blank Slide',
      subText: 'Ctrl + Enter',
      handler: createSlide,
    },
    {
      text: 'Edit Slide Name',
      subText: '',
      handler: showEditSlideNameDialog,
    },

    ...(lpiUser.value && currentSlide.value.elements.find(item => item.type === 'slidelayout')
        ? [
          {
            text: 'Apply Layout',
            handler: applyLayout,
          }
        ]
        : []),
    {
      text: 'Duplicate Slide',
      subText: 'Ctrl + D',
      handler: copyAndPasteSlide,
    },
    {
      text: 'Delete',
      subText: 'Delete',
      handler: () => deleteSlide(),
    },
    { divider: true },
    {
      text: 'Slideshow',
      subText: 'F5',
      handler: enterScreening,
    },
  ]
}
</script>

<style lang="scss" scoped>
.thumbnails {
  border-right: solid 1px $borderColor;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  user-select: none;
}
.add-slide {
  height: 40px;
  font-size: 14px;
  cursor: pointer;
  z-index: 1;

  .btn {
    font-size: 12px;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: $lightGray;
    }
  }
  .select-btn {
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-left: 1px solid $borderColor;

    &:hover {
      background-color: $lightGray;
    }
  }

  .icon {
    margin-right: 3px;
    font-size: 14px;
  }
}
.thumbnail-list {
  padding: 5px 0;
  flex: 1;
  overflow: auto;
}
.thumbnail-item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 0;

  .thumbnail {
    outline: 1px solid rgba($color: $themeColor, $alpha: .15);
  }

  &.active {
    .label {
      color: $themeColor;
    }
    .thumbnail {
      outline-color: $themeColor;
    }
  }
  &.selected {
    .thumbnail {
      outline-color: $themeColor;
    }
  }
}
.label {
  font-size: 12px;
  color: #999;
  width: 20px;
  cursor: grab;

  &.offset-left {
    position: relative;
    left: -4px;
  }

  &:active {
    cursor: grabbing;
  }
}
.page-number {
  height: 40px;
  font-size: 12px;
  border-top: 1px solid $borderColor;
  line-height: 40px;
  text-align: center;
  color: #666;
}

:deep(.p-panelmenu-header-action) {
  justify-content: center;
}

:deep(.p-icon.p-submenu-icon) {
  display: none;
}

:deep(.p-panelmenu .p-panelmenu-header .p-panelmenu-header-content .p-panelmenu-header-action) {
  padding: 0.5rem;
}

:deep(.p-panelmenu-content > ul) {
  margin-bottom: 0;
}

</style>
